/**
 * Secure Storage Utility
 * Provides data minimization and safe storage practices
 *
 * SECURITY PRINCIPLE: Store only minimal, non-sensitive data client-side
 * - Sensitive data (email, phone, address) should NEVER be stored in localStorage
 * - XSS vulnerabilities can expose anything in localStorage
 * - If you need sensitive data, fetch it from the backend API on demand
 */

/**
 * Minimal user data that's safe to store client-side
 * These fields are non-sensitive and needed for UI functionality
 */
export interface MinimalUserData {
  id: number;
  username: string;
  roles?: string[];
  // ⚠️ NEVER add sensitive fields here (email, phone, address, etc.)
}

/**
 * SecureStorage - Handles user data storage with security best practices
 */
class SecureStorage {
  private static USER_KEY = 'user';

  /**
   * Store minimal, non-sensitive user data
   * Only stores: id, username, roles
   *
   * @param userData - Full user object from backend
   */
  static setUserData(userData: any): void {
    if (!userData) {
      console.warn('setUserData called with null/undefined');
      return;
    }

    // Extract ONLY non-sensitive fields
    const minimalData: MinimalUserData = {
      id: userData.id,
      username: userData.username,
      roles: userData.roles || []
    };

    try {
      localStorage.setItem(this.USER_KEY, JSON.stringify(minimalData));

      // Clean up any old encrypted data from previous version
      this.cleanupLegacyData();
    } catch (error) {
      console.error('Failed to store user data:', error);
    }
  }

  /**
   * Get minimal user data
   * Returns only non-sensitive fields (id, username, roles)
   *
   * ⚠️ If you need sensitive data (email, phone), fetch from backend API
   */
  static getUserData(): MinimalUserData | null {
    try {
      const dataStr = localStorage.getItem(this.USER_KEY);
      if (!dataStr) return null;

      const data = JSON.parse(dataStr);

      // Validate that it has required fields
      if (!data.id || !data.username) {
        console.error('Invalid user data in storage');
        this.clearUserData();
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to parse user data:', error);
      this.clearUserData(); // Clear corrupted data
      return null;
    }
  }

  /**
   * Clear all user data from storage
   */
  static clearUserData(): void {
    localStorage.removeItem(this.USER_KEY);

    // Also clean up any legacy data
    this.cleanupLegacyData();
  }

  /**
   * Check if user is authenticated (has stored user data)
   */
  static isAuthenticated(): boolean {
    const useCookieAuth = process.env.REACT_APP_USE_COOKIE_AUTH === 'true';

    if (useCookieAuth) {
      // In cookie mode, check for user object
      return !!localStorage.getItem(this.USER_KEY);
    }

    // In legacy token mode, check for token or user
    return !!localStorage.getItem('token') || !!localStorage.getItem(this.USER_KEY);
  }

  /**
   * Clean up legacy encrypted data from old implementation
   * Removes old XOR-encrypted sensitive data that should no longer be stored
   */
  private static cleanupLegacyData(): void {
    try {
      // Remove old encrypted sensitive data
      localStorage.removeItem('_enc_user_sensitive');

      // Remove old encryption key
      sessionStorage.removeItem('_sk');

      // Remove old encrypted auth token (if cookie auth is enabled)
      const useCookieAuth = process.env.REACT_APP_USE_COOKIE_AUTH === 'true';
      if (useCookieAuth) {
        localStorage.removeItem('_auth');
      }
    } catch (error) {
      // Silently fail - cleanup is not critical
      console.debug('Legacy data cleanup failed:', error);
    }
  }

  /**
   * Get user ID from stored data
   * Convenience method for common use case
   */
  static getUserId(): number | null {
    const userData = this.getUserData();
    return userData?.id || null;
  }

  /**
   * Get username from stored data
   * Convenience method for common use case
   */
  static getUsername(): string | null {
    const userData = this.getUserData();
    return userData?.username || null;
  }

  /**
   * Check if user has a specific role
   * @param role - Role to check (e.g., 'admin', 'moderator')
   */
  static hasRole(role: string): boolean {
    const userData = this.getUserData();
    return userData?.roles?.includes(role) || false;
  }

  /**
   * Check if user is an admin
   */
  static isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * ⚠️ LEGACY METHOD - For backward compatibility only
   * Get auth token from localStorage (non-cookie mode)
   *
   * NOTE: Cookie auth mode is recommended - tokens in HttpOnly cookies
   * are more secure than localStorage (immune to XSS attacks)
   */
  static getAuthToken(): string | null {
    const useCookieAuth = process.env.REACT_APP_USE_COOKIE_AUTH === 'true';

    if (useCookieAuth) {
      // Token is in httpOnly cookie, not accessible to JavaScript
      return null;
    }

    // Legacy mode: token stored in localStorage
    return localStorage.getItem('token');
  }

  /**
   * ⚠️ LEGACY METHOD - For backward compatibility only
   * Set auth token in localStorage (non-cookie mode)
   *
   * NOTE: This should only be used if cookie auth is disabled
   * Cookie auth is more secure and is the recommended approach
   */
  static setAuthToken(token: string | null): void {
    const useCookieAuth = process.env.REACT_APP_USE_COOKIE_AUTH === 'true';

    if (useCookieAuth) {
      // Don't store token in localStorage when using cookie auth
      return;
    }

    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  /**
   * Clear auth token from localStorage
   */
  static clearAuthToken(): void {
    localStorage.removeItem('token');
  }
}

/**
 * Data Minimization Utilities
 * Helps ensure sensitive data doesn't accidentally get stored
 */
export class DataMinimizer {
  /**
   * Remove sensitive fields from user object before storage
   * Returns only safe, non-sensitive fields
   */
  static minimizeUserData(user: any): MinimalUserData {
    return {
      id: user.id,
      username: user.username,
      roles: user.roles || []
    };
  }

  /**
   * Check if a field name is considered sensitive
   * Used to prevent accidental storage of PII
   */
  static isSensitiveField(fieldName: string): boolean {
    const sensitiveFields = [
      'email', 'password', 'token', 'ssn', 'creditCard',
      'firstName', 'lastName', 'fullName', 'name',
      'phone', 'phoneNumber', 'mobile', 'telephone',
      'address', 'street', 'city', 'state', 'zip', 'zipcode', 'postal', 'country',
      'dateOfBirth', 'dob', 'birthDate', 'birthday',
      'bankAccount', 'routingNumber', 'iban', 'swift',
      'medicalInfo', 'healthInfo', 'medical', 'health',
      'taxId', 'ein', 'vat',
      'passport', 'license', 'driversLicense',
      'salary', 'income', 'wage',
      'apiKey', 'secretKey', 'privateKey', 'accessToken', 'refreshToken',
      'csrf', 'session', 'cookie'
    ];

    return sensitiveFields.some(field =>
      fieldName.toLowerCase().includes(field.toLowerCase())
    );
  }

  /**
   * Sanitize an object by removing all sensitive fields
   * Use this before logging or storing data
   *
   * @param data - Object to sanitize
   * @returns Object with sensitive fields removed
   */
  static sanitizeForStorage(data: any): any {
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeForStorage(item));
    }

    const sanitized: any = {};

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (!this.isSensitiveField(key)) {
          // Recursively sanitize nested objects
          if (typeof data[key] === 'object' && data[key] !== null) {
            sanitized[key] = this.sanitizeForStorage(data[key]);
          } else {
            sanitized[key] = data[key];
          }
        } else {
          // Replace sensitive value with redacted marker
          sanitized[key] = '[REDACTED]';
        }
      }
    }

    return sanitized;
  }

  /**
   * Validate that an object doesn't contain sensitive data
   * Returns true if safe to store, false if contains sensitive fields
   */
  static isSafeToStore(data: any): boolean {
    if (typeof data !== 'object' || data === null) {
      return true;
    }

    // Check all keys
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (this.isSensitiveField(key)) {
          console.warn(`⚠️ Sensitive field detected: ${key}`);
          return false;
        }

        // Recursively check nested objects
        if (typeof data[key] === 'object' && data[key] !== null) {
          if (!this.isSafeToStore(data[key])) {
            return false;
          }
        }
      }
    }

    return true;
  }
}

// Auto-cleanup legacy data on load
if (typeof window !== 'undefined') {
  // Clean up old encrypted data from previous implementation
  try {
    localStorage.removeItem('_enc_user_sensitive');
    sessionStorage.removeItem('_sk');

    // If cookie auth is enabled, remove old encrypted token
    if (process.env.REACT_APP_USE_COOKIE_AUTH === 'true') {
      localStorage.removeItem('_auth');
    }
  } catch (error) {
    // Silently fail - cleanup is not critical
  }
}

export default SecureStorage;
